import { useEffect, useMemo, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { catalogDefs } from "../local/db";

// card #1664 — the global test catalog (~148 rows, GET /catalog, rarely
// changes) is cached in IndexedDB (local/db.js), same pattern as
// scoreadmin-fe's useCatalogDefs (card #1662). This is a DEMOTION in
// priority order, not a swap (catalog-authority.md section 5, and the card
// description): #1665 retired the build-time score-common/catalog bundle
// that used to be the cold-start fallback for a scribe kiosk whose PWA had
// never been online since install; the fallback is now a caller-supplied
// name off the ride's own Test row (see classDisplay.js's
// resolveCatalogTestName / rideTestName, the only place this hook's result
// actually feeds into rendered UI) — hydration failure here is
// silent-to-scoring, never fatal, never blocking either way.
//
// REFRESH POLICY deliberately FORKS from scoreadmin-fe's useCatalogDefs
// (#1662), which hydrates once-if-empty and then trusts the cache forever.
// scoreadmin is where defs get EDITED and a reload there is cheap and
// expected of the person doing the editing; scribe is the CONSUMER that has
// to pick up an admin's correction with no redeploy (card #1664 AC2, driver
// decision recorded on the card). So scribe-fe refetches GET /catalog on
// EVERY app-init (once per page load / PWA restart) rather than only when
// the store is empty. Reads stay cache-first at ALL times regardless: the
// last-known-good rows are served immediately, and a hung or failed
// background refetch (the standard ring-side captive-portal failure) never
// blanks or replaces them — it degrades to exactly today's cache-only
// behavior, so every offline/mid-sheet guarantee (AC3, AC4) is untouched.
// Do NOT "fix" this back to scoreadmin's once-if-empty policy; the fork is
// intentional, not drift.
//
// `hydratePromise` is a MODULE-level (not React-state) in-flight guard: two
// callers mounting at the same moment (e.g. ScoreContext's app-init call and
// a page reading the hook for its own render) dedupe onto the same
// read-then-refetch instead of both racing a GET /catalog call that would
// try to seed the same rows twice. `currentDefs` + `listeners` are the
// module-level cache-first store: every mounted hook instance subscribes to
// it, so a background refetch that lands AFTER a component has already
// rendered (e.g. a ScoringForm open mid-sheet) reactively picks up the
// correction — but a FAILED or still-pending refetch simply never notifies,
// leaving every subscriber's last-known-good rows exactly as they were.
let hydratePromise = null;
let currentDefs = null;
const listeners = new Set();

function notify() {
  for (const fn of listeners) fn(currentDefs ?? []);
}

function hydrateOnce(api) {
  if (!hydratePromise) {
    hydratePromise = (async () => {
      // Cache-first: serve whatever IndexedDB already holds immediately, so
      // a slow or hung network can never delay (let alone block) the first
      // render of a def name.
      const cached = await catalogDefs.getAll();
      currentDefs = cached;
      notify();

      // Always attempt a background refetch (AC2 — deliberately NOT gated
      // on `cached` being non-empty), but never let it blank/replace the
      // cache on failure, and never propagate its rejection past this
      // function — nothing awaiting hydrateOnce() should ever see a catalog
      // hiccup as anything other than "still have the cache-first rows".
      try {
        const fetched = unwrap(await api.catalog.listCatalog());
        // No sentinel: a GET /catalog response that comes back genuinely
        // empty simply leaves the existing cache untouched (nothing to
        // put), so the NEXT app-init retries too rather than a transient
        // blank response permanently wiping a good cache (see #1662 round 3
        // / local/db.js's catalogDefs comment for the same tradeoff made
        // there).
        if (fetched.length) {
          await catalogDefs.putAll(fetched);
          currentDefs = fetched;
          notify();
        }
      } catch {
        // Silent (AC4, #1289's AuthGate lesson): the existing cache — from
        // IndexedDB, possibly stale, possibly still empty on a true cold
        // start — stays exactly as it was. resolveCatalogTestName's
        // caller-supplied fallback (off the ride's own Test row, #1665)
        // means a scribe's scoring screen is unaffected either way.
      }
      return cached;
    })();
  }
  return hydratePromise;
}

export function useCatalogDefs() {
  const api = useScoreApi();
  const [defs, setDefs] = useState(() => currentDefs ?? []);

  useEffect(() => {
    listeners.add(setDefs);
    // A component mounting after hydration already resolved (e.g. routing
    // to a new page mid-session) picks up whatever the module already knows
    // synchronously, without waiting on a fresh fetch.
    setDefs(currentDefs ?? []);
    hydrateOnce(api);
    return () => {
      listeners.delete(setDefs);
    };
  }, [api]);

  // O(1) lookup by def id for the display-name resolvers (classDisplay.js).
  const catalogDefsById = useMemo(() => {
    const byId = {};
    for (const def of defs) {
      if (def && def.id != null) byId[def.id] = def;
    }
    return byId;
  }, [defs]);

  return { catalogDefs: defs, catalogDefsById };
}
